import { useState, useCallback, useRef, useEffect } from 'react'
import type * as jsforce from 'jsforce'
import type { PRSheetData } from '../types/salesforce'
import type { LanguageSkill, FixedSkillRating } from '../components/SkillsEditor/types'
import {
  savePRSheet,
  savePRSkills,
  savePRExperiences,
  savePRPortfolioItems,
  savePRHighlightEpisodes,
  savePRAwards,
  savePREducations,
  savePRLinks,
  fetchPRSheetData,
} from '../services/salesforce'
import { mergeAllSkills } from '../utils/skillsHelper'

// グローバルfetchのオリジナルを保存（一度だけ）
const fetchOriginal = window.fetch

// 現在アクティブなAbortControllerを保持するグローバル変数
let currentAbortController: AbortController | null = null

// fetchをラップしてAbortSignalを自動的に追加
const fetchWithAbort: typeof fetch = async (input, init) => {
  const signal = currentAbortController?.signal
  // 既存のsignalがある場合は尊重する
  if (init?.signal) {
    return fetchOriginal(input, init)
  }
  return fetchOriginal(input, { ...init, signal })
}

// グローバルfetchを一度だけオーバーライド
if (window.fetch === fetchOriginal) {
  window.fetch = fetchWithAbort
}

/**
 * PRSheet保存処理フック
 *
 * 責務:
 * - PRSheetとすべての関連データの保存ロジック
 * - 保存状態の管理（saving フラグ）
 * - refを使って最新のデータを参照（stale closure回避）
 * - AbortControllerで進行中の保存をキャンセル（グローバルfetchをオーバーライド）
 */
export function useSaveHandler(
  dataRef: React.MutableRefObject<PRSheetData | null>,
  fixedSkillRatingsRef: React.MutableRefObject<Record<string, FixedSkillRating>>,
  languageSkillsRef: React.MutableRefObject<LanguageSkill[]>,
  deletedSkillIds: string[],
  deletedExperienceIds: string[],
  deletedPortfolioItemIds: string[],
  deletedHighlightEpisodeIds: string[],
  deletedAwardIds: string[],
  deletedEducationIds: string[],
  deletedLinkIds: string[],
  onSuccess: (data: PRSheetData) => void,
  onError: (message: string, isSessionExpired: boolean) => void,
  onClearDeletedIds: () => void,
) {
  const [saving, setSaving] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // 進行中の保存をキャンセル
  const abortSave = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('保存をキャンセルします')
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      currentAbortController = null
      setSaving(false)
    }
  }, [])

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      abortSave()
    }
  }, [abortSave])

  const handleSave = useCallback(
    async (conn: jsforce.Connection) => {
      // 進行中の保存をキャンセル
      abortSave()

      // refから最新のデータを取得
      const currentData = dataRef.current

      if (!currentData) return

      // 新しいAbortControllerを作成
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      currentAbortController = abortController // グローバル変数にも設定

      setSaving(true)
      try {
        // PRSheet保存
        const prSheetId = await savePRSheet(conn, currentData.prSheet)

        // PRSheet.Idを更新（新規作成の場合）
        if (!currentData.prSheet.Id && prSheetId) {
          currentData.prSheet.Id = prSheetId
        }

        // 固定スキル、言語スキル、既存スキルをマージして保存
        const currentFixedSkillRatings = fixedSkillRatingsRef.current
        const currentLanguageSkills = languageSkillsRef.current
        const { mergedSkills, deletedSkillIds: skillsToDelete } = mergeAllSkills(
          currentFixedSkillRatings,
          currentLanguageSkills,
          currentData.skills,
          prSheetId,
        )

        // スキル保存（マージされた全スキルを保存）
        await savePRSkills(conn, mergedSkills, [...deletedSkillIds, ...skillsToDelete], new Map())

        // NOTE: Career__cのスキルフィールドは Apex トリガー（PRSkillTrigger）で自動同期されるため、
        // React側からの直接更新は不要になりました

        // 新規オブジェクトの保存
        await Promise.all([
          savePRExperiences(conn, currentData.experiences, deletedExperienceIds),
          savePRPortfolioItems(conn, currentData.portfolioItems, deletedPortfolioItemIds),
          savePRHighlightEpisodes(conn, currentData.highlightEpisodes, deletedHighlightEpisodeIds),
          savePRAwards(conn, currentData.awards, deletedAwardIds),
          savePREducations(conn, currentData.educations, deletedEducationIds),
          savePRLinks(conn, currentData.links, deletedLinkIds),
        ])

        // 削除IDリストをクリア
        onClearDeletedIds()

        // データを再読み込みして最新状態を反映
        const refreshedData = await fetchPRSheetData(conn, currentData.career.Id!)

        // abortされていない場合のみ成功処理
        if (!abortController.signal.aborted) {
          onSuccess(refreshedData)
        }
      } catch (error) {
        // abortされた場合は何もしない
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('保存がキャンセルされました')
          return
        }

        console.error('保存エラー:', error)

        // セッションエラーの場合はログイン画面にリダイレクト
        const errorMessage = error instanceof Error ? error.message : String(error)
        const isSessionExpired = errorMessage.includes('INVALID_SESSION_ID') || errorMessage.includes('Session expired')

        onError('保存に失敗しました', isSessionExpired)
      } finally {
        // abortされていない場合のみsaving状態を解除
        if (!abortController.signal.aborted) {
          setSaving(false)
        }
        // Clean up references to prevent memory leaks
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
        if (currentAbortController === abortController) {
          currentAbortController = null
        }
      }
    },
    [
      abortSave,
      dataRef,
      fixedSkillRatingsRef,
      languageSkillsRef,
      deletedSkillIds,
      deletedExperienceIds,
      deletedPortfolioItemIds,
      deletedHighlightEpisodeIds,
      deletedAwardIds,
      deletedEducationIds,
      deletedLinkIds,
      onSuccess,
      onError,
      onClearDeletedIds,
    ],
  )

  return {
    saving,
    handleSave,
    abortSave,
  }
}
